import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import classNames from 'classnames';

import {
    Colours,
    Sizes,
    Paragraph,
    Typography,
    IconSearch,
    IconPaper,
    PillCaption,
    PageBehaviourButton,
} from '@ratehub/base-ui';
import { CREDIT_CARD_DOC_TYPES } from '@ratehub/documents/cc-journey-document/stable';


dayjs.extend(advancedFormat);

/* eslint-disable react/no-multi-comp */
function CreditCardApplicationRow({ document, isMostRecent, creditCardDocumentType }) {
    const intl = useIntl();

    const { modified, id, resumeHREF } = document ?? {};

    const isToday = document ? dayjs(modified).isSame(dayjs(), 'day') : null;
    const dateFormat = document ? dayjs(modified).format('MMMM Do, YYYY') : null;
    const timeFormat = document ? dayjs(modified).format('h:mma') : null;

    const lastModifiedLabelId = `${id}-last-modified-label`;
    const cardNameLabelId = `${id}-card-name-label`;

    return (
        <Container
            data-name="dashboard-credit-card-row"
        >
            <Choose>
                <When condition={!!document}>
                    <If condition={isMostRecent}>
                        <PillCaption
                            className="pill"
                            backgroundColour={Colours.YUZU}
                            message={intl.formatMessage(Messages.MOST_RECENT)}
                        />
                    </If>
                    <div className="applicationRow">
                        <div className="applicationDetails applicationColumn">
                            <h4 className={
                                classNames(
                                    'address',
                                    Typography.CLASSES.TITLE_XS,
                                    Typography.CLASSES.WEIGHT_MEDIUM,
                                )}
                            >
                                <Choose>
                                    <When condition={creditCardDocumentType === CREDIT_CARD_DOC_TYPES.CARD_FINDER}>
                                        <FormattedMessage
                                            id="accounts.CreditCardApplicationRow.cardFinderRowTitle"
                                            defaultMessage="My Perfect Card"
                                        />
                                    </When>
                                    <When condition={creditCardDocumentType === CREDIT_CARD_DOC_TYPES.HOSTED_APP}>
                                        <FormattedMessage
                                            id="accounts.CreditCardApplicationRow.hostedAppRowTitle"
                                            defaultMessage="Credit Card"
                                        />
                                    </When>
                                </Choose>
                            </h4>
                            <div className="detailsLine">
                                <p className={
                                    classNames(
                                        Typography.CLASSES.TEXT_XS,
                                        Typography.CLASSES.LEADING_L,
                                    )}
                                >
                                    <Choose>
                                        <When condition={creditCardDocumentType === CREDIT_CARD_DOC_TYPES.CARD_FINDER}>
                                            <FormattedMessage
                                                id="accounts.CreditCardApplicationRow.cardFinderLabel"
                                                defaultMessage="Resume the search for your perfect card"
                                            />
                                        </When>
                                        <When condition={creditCardDocumentType === CREDIT_CARD_DOC_TYPES.HOSTED_APP}>
                                            <FormattedMessage
                                                id="accounts.CreditCardApplicationRow.hostedAppLabel"
                                                defaultMessage="<label>Card: </label><value>{cardName}</value>"
                                                values={{
                                                    label: chunks => (
                                                        <span
                                                            className="detailsLabel"
                                                            id={cardNameLabelId}
                                                        >
                                                            {chunks}
                                                        </span>
                                                    ),
                                                    value: chunks => (
                                                        <span
                                                            className="detailsValue"
                                                            aria-labelledby={cardNameLabelId}
                                                        >
                                                            {chunks}
                                                        </span>
                                                    ),
                                                    cardName: document.cardName,
                                                }}
                                            />
                                        </When>
                                    </Choose>
                                </p>
                            </div>
                            <div className="detailsLine">
                                <p className={
                                    classNames(
                                        Typography.CLASSES.TEXT_XS,
                                        Typography.CLASSES.WEIGHT_MEDIUM,
                                        Typography.CLASSES.LEADING_L,
                                    )}
                                >
                                    <FormattedMessage
                                        id="accounts.CreditCardApplicationRow.lastModifiedLabel"
                                        defaultMessage="<label>Last modified: </label>
                                            <value>{isToday, select,
                                                true {Today, {timeFormat}}
                                                other {{dateFormat}}
                                            }</value>
                                        "
                                        values={{
                                            label: chunks => (
                                                <span
                                                    className="detailsLabel"
                                                    id={lastModifiedLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            value: chunks => (
                                                <span
                                                    className="detailsValue"
                                                    aria-labelledby={lastModifiedLabelId}
                                                >
                                                    {chunks}
                                                </span>
                                            ),
                                            timeFormat: timeFormat,
                                            dateFormat: dateFormat,
                                            isToday: isToday,
                                        }}
                                    />
                                </p>
                            </div>
                        </div>
                        <div className="applicationActions applicationColumn">
                            {/* Note that action button will default to application if creditCardDocumentType is unexpected */}
                            <PageBehaviourButton
                                onClick={() => window.location.assign(resumeHREF)}
                                variant="dark"
                                size="small"
                                iconLocation="left"
                                iconComponent={
                                    creditCardDocumentType === CREDIT_CARD_DOC_TYPES.CARD_FINDER
                                        ? IconSearch
                                        : IconPaper
                                }
                                includeIconOutline={false}
                                message={(
                                    <FormattedMessage
                                        {
                                            ...creditCardDocumentType === CREDIT_CARD_DOC_TYPES.CARD_FINDER
                                                ? Messages.RESUME_SEARCH
                                                : Messages.RESUME_APPLICATION
                                        }
                                    />
                                )}
                                data-name={
                                    creditCardDocumentType === CREDIT_CARD_DOC_TYPES.CARD_FINDER
                                        ? 'dashboard-credit-cards-resumeSearch'
                                        : 'dashboard-credit-cards-resumeApplication'
                                }
                            />
                        </div>
                    </div>
                </When>
                <Otherwise>
                    <Paragraph
                        className="no-document"
                        size="medium"
                        message={Messages.NO_CREDIT_CARDS}
                    />
                </Otherwise>
            </Choose>
        </Container>
    );
}
CreditCardApplicationRow.propTypes = {
    document: PropTypes.shape({
        id: PropTypes.string,
        modified: PropTypes.string,
        resumeHREF: PropTypes.string,
        cardName: PropTypes.string,
    }),
    isMostRecent: PropTypes.bool,
    creditCardDocumentType: PropTypes.oneOf(Object.values(CREDIT_CARD_DOC_TYPES)).isRequired,
};

CreditCardApplicationRow.defaultProps = {
    document: undefined,
    isMostRecent: false,
};

const Container = styled.section`
    display: flex;
    flex-direction: column;
    padding: ${Sizes.SPACING.ONE_AND_A_HALF} ${Sizes.SPACING.ONE};
    > .pill {
        width: fit-content;
        font-weight: ${Typography.WEIGHTS.MEDIUM};
        font-size: ${Sizes.FONTS['2XS']};
        margin: 0 0 ${Sizes.SPACING.THREE_QUARTERS};
    }
    > .applicationRow {
        justify-content: space-between;
        flex-direction: row;
        flex-wrap: wrap;
        display: flex;
        > .applicationColumn {
            &:first-child {
                margin-right: ${Sizes.SPACING.ONE_AND_A_HALF};
            }
            &.applicationDetails {
                min-width: 200px;
                margin: ${Sizes.SPACING.THREE_QUARTERS} 0 ${Sizes.SPACING.ONE};
                > .address {
                    font-size: ${Sizes.FONTS.M};
                    font-weight: ${Typography.WEIGHTS.MEDIUM};
                    text-transform: capitalize;
                    margin: 0 0 ${Sizes.SPACING.ONE_AND_A_HALF};
                }
                > .detailsLine {
                    p {
                        margin: 0;
                        font-size: ${Sizes.FONTS['2XS']};
                        line-height: ${Sizes.LINE_HEIGHT.XL}
                    }
                    .detailsLabel {
                        text-transform: uppercase;
                        color: ${Colours.STONE_DARKEST};
                    }
                    .detailsValue {
                        font-weight: ${Typography.WEIGHTS.MEDIUM};
                    }
                    .detailsValue.type {
                        text-transform: uppercase;
                    }
                }
            }
            &.applicationActions {
                min-width: 250px;
                > button {
                    min-height: 48px;

                    > .applicationIcon {
                        width: 2.5rem;
                        margin-left: -10px;
                    }
                    &:hover {
                        > .applicationIcon {
                            stroke: ${Colours.BLUEBERRY};
                        }
                    }
                }
            }
        }
    }
    .no-document {
        text-align: center;
        margin: ${Sizes.SPACING.ONE_AND_A_HALF};
        color: ${Colours.STONE_DARKEST};
    }
    .document {
        margin: ${Sizes.SPACING.TWO};
        height: ${Sizes.SPACING.SEVEN};
        color: ${Colours.BLUEBERRY};
    }
`;

const Messages = defineMessages({
    MOST_RECENT: {
        id: 'accounts.CreditCardApplicationRow.mostRecentPill',
        defaultMessage: 'Your most recent application',
    },
    RESUME_SEARCH: {
        id: 'AccountApp.CreditCardApplicationRow.resumeSearch',
        defaultMessage: 'resume search',
    },
    RESUME_APPLICATION: {
        id: 'AccountApp.CreditCardApplicationRow.resumeApplication',
        defaultMessage: 'resume application',
    },
    NO_CREDIT_CARDS: {
        id: 'AccountApp.CreditCardApplicationRow.noCreditCards',
        defaultMessage: 'You have no credit card searches in progress.',
    },
});

export default observer(CreditCardApplicationRow);
